/**
 * userAccessApi.js — API client for Aria One per-org User-Access mapping
 * (first-login bootstrap + admin management).
 *
 * Endpoints:
 *   GET    /api/user-access/me              — Resolve caller's app_role (bootstraps on first login)
 *   GET    /api/user-access                 — List mappings (team:admin)
 *   POST   /api/user-access                 — Invite/pre-provision by email (team:admin)
 *   PUT    /api/user-access/:id/role        — Change role (team:admin)
 *   PATCH  /api/user-access/:id/status      — Activate/deactivate (team:admin)
 *   DELETE /api/user-access/:id             — Delete a mapping (team:admin)
 */
import { get, post, put, patch, del, requireParam } from './httpClient';

const BASE = '/api/user-access';

/** Resolve the caller's effective app_role. Triggers first-login bootstrap. */
export const whoami = () => get(`${BASE}/me`);

/** List all mappings for the caller's org. Optional status filter. */
export const listMappings = (filters = {}) => get(`${BASE}`, filters);

/**
 * Invite / pre-provision a user by email with a target role.
 * @param {Object} payload — { email, app_role, auth_user_id? }
 */
export const inviteUser = (payload) => {
  requireParam('email', payload?.email);
  requireParam('app_role', payload?.app_role);
  return post(`${BASE}`, payload);
};

/** Change an existing mapping's role. */
export const setRole = (mappingId, appRole) => {
  requireParam('mappingId', mappingId);
  requireParam('appRole', appRole);
  return put(`${BASE}/${mappingId}/role`, { app_role: appRole });
};

/** Activate or deactivate a mapping. */
export const setStatus = (mappingId, status) => {
  requireParam('mappingId', mappingId);
  requireParam('status', status);
  return patch(`${BASE}/${mappingId}/status`, { status });
};

/** Permanently delete a mapping. */
export const deleteMapping = (mappingId) => {
  requireParam('mappingId', mappingId);
  return del(`${BASE}/${mappingId}`);
};

export const APP_ROLES = Object.freeze([
  { id: 'super_admin', label: 'Super Admin', desc: 'Full platform access' },
  { id: 'admin',       label: 'Admin',       desc: 'Team + scheduler admin' },
  { id: 'front_desk',  label: 'Front Desk',  desc: 'Operations: book, check-in, waitlist' },
  { id: 'provider',    label: 'Provider',    desc: 'Clinical + operations' },
  { id: 'read_only',   label: 'Read-Only',   desc: 'View-only (no writes)' },
]);

export const roleLabelFor = (id) =>
  (APP_ROLES.find((r) => r.id === id) || {}).label || id;
